import { ReactNode, useEffect, useRef, useState } from "react";

interface LuxuryScrollProviderProps {
  children: ReactNode;
}

const LuxuryScrollProvider = ({ children }: LuxuryScrollProviderProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile, { passive: true });

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(motionQuery.matches);
    const handleMotionChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    motionQuery.addEventListener('change', handleMotionChange);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const el = barRef.current;
        if (!el) return;
        const h = document.documentElement.scrollHeight - window.innerHeight;
        const p = h > 0 ? window.scrollY / h : 0;
        el.style.transform = `scaleX(${p})`;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', checkMobile);
      motionQuery.removeEventListener('change', handleMotionChange);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  if (prefersReducedMotion) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        ref={barRef}
        className="fixed top-0 left-0 right-0 h-[2px] md:h-[3px] origin-left z-[100]"
        style={{
          transform: 'scaleX(0)',
          willChange: 'transform',
          background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent)))',
          boxShadow: isMobile ? 'none' : '0 0 15px hsla(270, 70%, 60%, 0.4)'
        }}
      />

      {!isMobile && (
        <div className="fixed top-0 left-0 right-0 h-20 bg-gradient-to-b from-background/40 to-transparent pointer-events-none z-40" />
      )}

      {children}
    </>
  );
};

export default LuxuryScrollProvider;
